import { observer } from 'mobx-react';
import { IAgtPlanungDoc } from '../DocumentViewer';
import { useStore } from 'app/context';
import { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useField, useFormikContext } from 'formik';
import { Button } from '../../common/Button';
import { IJpg25, IJpgThema, JpgThema } from 'app/stores/bnr.document.store';
import { runInAction } from 'mobx';
import { JpgThemaEditModal } from './JpgFocusThemaEdit';
import { FocusThemaVM } from 'app/stores/ui/document.ui.store';
import { ITableColumn, TableModel } from '../../table/table.model';
import { Table } from '../../table/table';
import { mapAllJpgThemenToVM, mapFocusThemenToVM } from 'app/stores/ui/jpg.themen.ui.store';
import { JpgZiele, JpgZieleEditField } from './JpgZiele';
import { JpgFocusThemaReadModal } from './JpgFocusThemaRead';
import { AgenturModel } from 'app/models/agentur.model';
import { Icon } from 'app/components/common/Icon';
import { TooltipPortal } from 'app/components/common/TooltipPortal';
import { findNachfolgeplanungAgtItem } from './focusThemen.utils';

export const JpgFocusThemen = observer((props: IAgtPlanungDoc) => {
	const { documentUiStore, uiStore } = useStore();
	const isEditing = props.doc.doc.id === documentUiStore.currentDocId;
	const [focusThemen, setFocusThemen] = useState<FocusThemaVM[]>();
	const [ziele, setZiele] = useState<string[]>([]);

	useEffect(() => {
		setFocusThemen(props.doc.focusThemen);
		setZiele(props.doc.jpgZiele);
	}, [props.doc]);
	return (
		<>
			{!isEditing && focusThemen && (
				<>
					{ziele.length > 0 && props.doc.isJpg25ZieleVisible && (
						<>
							<ZieleIntro />
							<div className="ziele">
								<JpgZiele ziele={ziele} />
							</div>
						</>
					)}
					{focusThemen.length > 0 && !uiStore.printMode && (
						<div className="fokusthemen">
							<div className="fokusthemen-table-container">
								<strong className="title">Fokusthemen</strong>
								<JpgFocusThemenTable
									editMode={isEditing}
									data={focusThemen}
									currentFocusThemen={focusThemen.map((x) => x.focusThema!)}
									mode="focusThemen"
									agt={props.doc.agt!}
									year={props.doc.planYear!}
									doc={props.doc}
								/>
							</div>
						</div>
					)}
				</>
			)}
			{isEditing && (
				<>
					<JpgZieleEditField {...props} />
					<FocusThemenEditorField />
				</>
			)}
		</>
	);
});

export const ZieleIntro = observer(() => {
	return (
		<div className="ziele-intro">
			<strong>Ziele</strong> <br />
			<span>Was will ich konkret in diesem Jahr erreichen?</span>
		</div>
	);
});

export interface IFocusThema {
	key: string;
	status: string;
	notiz: string;
}

export const FocusThemenEditorField = () => {
	const { documentUiStore } = useStore();
	const { setFieldValue } = useFormikContext();
	const [field] = useField('meta');
	const meta = field.value as IJpg25;
	const currentDoc = documentUiStore.current!;

	const [focusThemen, setFocusThemen] = useState<JpgThema[]>([]);
	const [selectedFocusThemen, setSelectedFocusThemen] = useState<FocusThemaVM[]>([]);
	const [allFocusThemen, setaAllFocusThemen] = useState<FocusThemaVM[]>([]);

	const [availableFocusThemen, setAvailableFocusThemen] = useState<FocusThemaVM[]>([]);

	const collectFocusThemenState = () => {
		const fc = meta.focusThemen ? meta.focusThemen : [];
		setFocusThemen(fc as JpgThema[]);
		let s = mapFocusThemenToVM(fc as JpgThema[], currentDoc.doc.planYear!, currentDoc.doc.adzEmpfehlung);
		setSelectedFocusThemen(s);
		let all = mapAllJpgThemenToVM(focusThemen as JpgThema[], currentDoc.doc.planYear!, currentDoc.doc.adzEmpfehlung);
		setaAllFocusThemen(all);
		all = all.filter((x) => x.def!!);
		s = s.filter((x) => x.def!!);
		const selectedKeys = s.map((x) => x.def.keyThema);
		const avail = all.filter((x) => !selectedKeys.includes(x.def.keyThema));
		setAvailableFocusThemen(avail);
	};

	useEffect(() => {
		collectFocusThemenState();
	}, []);

	const onChangeData = () => {
		if (!documentUiStore.currentFocusThemaEdit) {
			return;
		}
		const t = documentUiStore.currentFocusThemaEdit;

		if (!meta.focusThemen) {
			meta.focusThemen = [];
		}
		const existingIndex = meta.focusThemen.findIndex((item: IJpgThema) => item.key === t.key);
		if (existingIndex > -1) {
			meta.focusThemen[existingIndex] = t;
		} else {
			meta.focusThemen.push(t);
		}
		setFieldValue('meta', meta);
		collectFocusThemenState();
	};

	const onDelete = () => {
		if (!documentUiStore.currentFocusThemaEdit) {
			return;
		}
		const t = documentUiStore.currentFocusThemaEdit;
		if (!meta.focusThemen) {
			meta.focusThemen = [];
		}
		meta.focusThemen = meta.focusThemen.filter((item: IJpgThema) => item.key !== t.key);
		setFieldValue('meta', meta);
		collectFocusThemenState();
	};

	return (
		<>
			<div className="fokusthemen">
				{focusThemen.length > 0 && (
					<div className="fokusthemen-table-container">
						<strong className="title">Fokusthemen</strong>
						<JpgFocusThemenTable data={selectedFocusThemen} editMode={true} currentFocusThemen={focusThemen} mode="focusThemen" agt={currentDoc.agt} year={currentDoc.doc.planYear} />
					</div>
				)}
				<div className="fokusthemen-table-container">
					<strong className="title">Verfügbare Themen</strong>
					<JpgFocusThemenTable data={availableFocusThemen} editMode={true} currentFocusThemen={focusThemen} mode="all" agt={currentDoc.agt} year={currentDoc.doc.planYear} />
				</div>

				<JpgThemaEditModal onChange={onChangeData} onDelete={onDelete} />
			</div>
		</>
	);
};

interface IJpgFocusThemenTable {
	data: FocusThemaVM[];
	editMode: boolean;
	currentFocusThemen: JpgThema[]; // needed for edit
	mode: 'all' | 'focusThemen';
	agt?: AgenturModel;
	year?: number;
	doc?: import('app/stores/ui/document.ui.store').DocumentVM;
}
export const JpgFocusThemenTable = observer((props: IJpgFocusThemenTable) => {
	const { uiStore, documentUiStore, nachfolgeplanungUiStore } = useStore();

	const onEditClick = (key: string, isNew: boolean) => {
		let t: JpgThema | undefined;
		if (props.currentFocusThemen) {
			t = props.currentFocusThemen.find((t: JpgThema) => t.key === key);
		}

		if (!t) {
			t = new JpgThema({ key });
		}

		runInAction(() => {
			documentUiStore.currentFocusThemaEdit = t;
			documentUiStore.currentFocusThemaEditIsNew = isNew;
		});

		// Check if VBL user should get edit mode
		const shouldEdit = props.editMode || (props.doc && props.doc.isVblJpg25 && props.doc.doc.editable);

		if (shouldEdit) {
			uiStore.showModal(uiStore.modalIds.jpgThemaEdit);
		} else {
			uiStore.showModal(uiStore.modalIds.jpgThemaView);
		}
	};

	const tm = useMemo(() => {
		const tm = new TableModel<FocusThemaVM, number>();
		const cols: ITableColumn<FocusThemaVM, number>[] = [];

		cols.push({
			label: '',
			path: `def.color`,
			cellModifier: 'is-graphic-cell',
			labelModifier: 'is-graphic-cell',
			render: (row) => {
				return <div className="color-bar" style={{ backgroundColor: row.data.def.background || '' }}></div>;
			},
		});

		cols.push({
			label: 'Gruppe',
			path: `def.group`,
			cellModifier: 'group-cell',
			render: (row) => {
				return (
					<div className="align-vertical">
						<strong>{row.data.def.group}</strong>{' '}
						{row.data.adzEmfephlung && (
							<TooltipPortal tooltip="Empfehlung Fitnessplan" position="right">
								<Icon iconClass="star" faModifier="color-warning" />
							</TooltipPortal>
						)}
					</div>
				);
			},
		});

		cols.push({
			label: 'Thema',
			path: `def.thema`,
			cellModifier: 'thema-cell',
			render: (row) => {
				return (
					<>
						<strong>{row.data.def.thema}</strong> <br />
						<span className="color-grey print-none">{row.data.def.byLine}</span>
					</>
				);
			},
		});
		if (!props.editMode && uiStore.printMode === true) {
			cols.push({
				label: 'Notiz',
				path: `focusThema.notiz`,
				cellModifier: 'thema-notiz',
			});
		}
		if (props.mode === 'focusThemen') {
			cols.push({
				label: '',
				path: `focusThema.share`,
				cellModifier: 'thema-share',
				render: (row) => {
					return (
						<>
							{row.data.focusThema?.share && row.data.focusThema?.share.length > 0 ? (
								<TooltipPortal tooltip={`Geteilt mit ${row.data.focusThema?.share.length}`} position="right">
									<Icon iconClass="user-friends" />
								</TooltipPortal>
							) : (
								<></>
							)}
						</>
					);
				},
			});
		}
		if (props.editMode) {
			cols.push({
				label: 'Notiz',
				path: `focusThema.notiz`,
			});
		}
		if (props.editMode) {
			cols.push({
				label: '',
				path: `edit`,
				render: (row) => {
					return (
						<>
							{!row.data.isFocusThema && props.mode === 'all' && (
								<Button type="button" className="button is-small is-inverted" onClick={() => onEditClick(row.data.def.keyThema, true)}>
									Hinzufügen
								</Button>
							)}

							{props.mode === 'focusThemen' && (
								<Button type="button" className="button is-small is-inverted is-primary" onClick={() => onEditClick(row.data.def.keyThema, false)}>
									Bearbeiten
								</Button>
							)}
						</>
					);
				},
			});
		}

		tm.onRowClick = (row) => {
			onEditClick(row.data.def.keyThema, props.mode === 'all');
		};

		tm.sortBy = 'def.sort';
		tm.setCols(cols);

		tm.sortDisabled = true;
		tm.idProperty = 'agtId';
		tm.idType = 'number';

		return tm;
	}, [props, props.data]);

	useEffect(() => {
		nachfolgeplanungUiStore.load().then(() => {
			const agtItem = findNachfolgeplanungAgtItem(nachfolgeplanungUiStore.items, props.agt?.agtId);
			// Sort data: ADZ recommendations (adzEmfephlung === true) get sort 0, others keep their original sort
			let sortedData = _.sortBy(props.data, (item) => {
				return item.adzEmfephlung ? 0 : (item.def && item.def.sort) || 999;
			}).filter((x) => x.def!!);
			if (agtItem && agtItem.inhaberAge >= 55) {
				// push nachfolgeplanung to the top if older than 55
				sortedData = _.sortBy(sortedData, (item) => {
					return item.def && item.def.group === 'Nachfolgeplanung' ? 0 : 1;
				});
			}
			tm.setRowData(sortedData);
		});
	}, [tm, props.data, props.agt, nachfolgeplanungUiStore, nachfolgeplanungUiStore.items]);

	// Callbacks for VBL auto-edit mode
	const handleFocusThemaChange = async () => {
		// For VBL users in read view who are now editing
		if (props.doc && props.doc.isVblJpg25 && !props.editMode) {
			const t = documentUiStore.currentFocusThemaEdit;
			if (!t) return;

			const meta = props.doc.doc.content.meta as IJpg25;
			if (!meta.focusThemen) {
				meta.focusThemen = [];
			}

			const existingIndex = meta.focusThemen.findIndex((item: IJpgThema) => item.key === t.key);
			if (existingIndex > -1) {
				meta.focusThemen[existingIndex] = t;
			} else {
				meta.focusThemen.push(t);
			}

			// Save the document after fokusthema change
			await documentUiStore.save(props.doc);
		}
	};

	const handleFocusThemaDelete = async () => {
		// For VBL users in read view who are now editing
		if (props.doc && props.doc.isVblJpg25 && !props.editMode) {
			const t = documentUiStore.currentFocusThemaEdit;
			if (!t) return;

			const meta = props.doc.doc.content.meta as IJpg25;
			if (!meta.focusThemen) {
				meta.focusThemen = [];
			}

			meta.focusThemen = meta.focusThemen.filter((item: IJpgThema) => item.key !== t.key);

			// Save the document after fokusthema deletion
			await documentUiStore.save(props.doc);
		}
	};

	return (
		<>
			<Table modifier="has-side-border last-col-right" tm={tm} />
			{props.agt && props.year && <JpgFocusThemaReadModal agt={props.agt} year={props.year} />}
			{props.doc && props.doc.isVblJpg25 && !props.editMode && <JpgThemaEditModal onChange={handleFocusThemaChange} onDelete={handleFocusThemaDelete} agt={props.agt} year={props.year} />}
		</>
	);
});

export const Jpg25LeitfadenButton = () => {
	return (
		<a className={`button is-inverted is-small is-primary`} href={`JPG25-Leitfaden.pdf`} title={'Leitfaden'} target="_blank" rel="noreferrer">
			Leitfaden
		</a>
	);
};

interface IJpgFocusThemenPrint {
	data?: FocusThemaVM[];
	editMode?: boolean;
	currentFocusThemen?: JpgThema[]; // needed for edit
	mode?: 'all' | 'focusThemen';
	agt?: AgenturModel;
	year?: number;
	doc?: import('app/stores/ui/document.ui.store').DocumentVM;
}
export const JpgFocusThemenPrint = observer((props: IJpgFocusThemenPrint) => {
	const { data } = props;

	if (!data) return <></>;

	return (
		data && (
			<>
				<div className="pad-1rem">
					<div className="content">
						<p>
							<strong>Fokusthemen</strong>
						</p>
					</div>

					{data.map((thema) => {
						return (
							<>
								<div className="focusthema-grid-item grid is-col-1-3" key={thema.def.thema}>
									<div className="fokusthema-details" style={{ borderLeftColor: thema.def.background || '' }}>
										<div>
											<span className="color-grey">Gruppe</span>
											<br />
											<strong>{thema.def.group}</strong>

											{thema.adzEmfephlung && <Icon iconClass="star" faModifier="color-warning" />}
										</div>
										<div>
											<span className="color-grey">Thema</span>
											<br />
											<strong>{thema.def.themaShort}</strong>
										</div>
										<div>
											<span className="color-grey">ByLine</span>
											<br />
											<strong>{thema.def.byLine}</strong>
										</div>
									</div>
									<div className="fokusthema-notiz">
										<strong>Notiz</strong>
										<br />
										<div>{thema.focusThema?.notiz ? <span>{thema.focusThema?.notiz}</span> : <span className="color-grey">Keine Notiz</span>}</div>
									</div>
								</div>
							</>
						);
					})}
				</div>
			</>
		)
	);
});
